/**
 * React Query hook — conversation inbox list with local cache + background refresh.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getConversationList } from "../services/conversationListService.js";
import {
  getConversationListCache,
  setConversationListCache,
} from "../utils/conversationCache.js";
import {
  SYNC_VARIANT,
  deriveConversationListSyncStatus,
  fingerprintConversationList,
} from "../utils/conversationSyncStatus.js";

export function conversationListQueryKey(filters = {}) {
  return [
    "conversation-list",
    filters.search || "",
    filters.status || "open",
    filters.source || "",
    filters.since || "",
    Boolean(filters.needsAttention),
    filters.page || 1,
    filters.pageSize || 25,
  ];
}

export function useConversationList(filters = {}, { enabled = true } = {}) {
  const normalizedFilters = useMemo(
    () => ({
      search: filters.search || undefined,
      status: filters.status || "open",
      source: filters.source || undefined,
      since: filters.since || undefined,
      needsAttention: Boolean(filters.needsAttention),
      page: filters.page || 1,
      pageSize: filters.pageSize || 25,
    }),
    [
      filters.search,
      filters.status,
      filters.source,
      filters.since,
      filters.needsAttention,
      filters.page,
      filters.pageSize,
    ]
  );

  const cacheEntry = useMemo(
    () => getConversationListCache(normalizedFilters),
    [normalizedFilters]
  );
  const hadCachedData = Boolean(cacheEntry?.data);
  const [transientNotice, setTransientNotice] = useState(null);
  const previousFingerprintRef = useRef(
    cacheEntry?.data ? fingerprintConversationList(cacheEntry.data) : null
  );
  const completedFetchRef = useRef(false);

  const query = useQuery({
    queryKey: conversationListQueryKey(normalizedFilters),
    queryFn: async () => {
      const result = await getConversationList(normalizedFilters);
      setConversationListCache(normalizedFilters, result);
      return result;
    },
    enabled,
    staleTime: 60_000,
    initialData: cacheEntry?.data,
    initialDataUpdatedAt: cacheEntry?.fetchedAt,
    retry: 1,
    keepPreviousData: true,
  });

  useEffect(() => {
    completedFetchRef.current = false;
    previousFingerprintRef.current = cacheEntry?.data
      ? fingerprintConversationList(cacheEntry.data)
      : null;
    setTransientNotice(null);
  }, [normalizedFilters, cacheEntry?.data]);

  useEffect(() => {
    if (!query.isSuccess || query.isFetching || !query.data) return;

    const fingerprint = fingerprintConversationList(query.data);
    const previous = previousFingerprintRef.current;

    if (completedFetchRef.current) {
      if (previous && previous !== fingerprint) {
        setTransientNotice(SYNC_VARIANT.UPDATED);
      } else {
        setTransientNotice(SYNC_VARIANT.UP_TO_DATE);
      }
    }

    previousFingerprintRef.current = fingerprint;
    completedFetchRef.current = true;
  }, [query.isSuccess, query.isFetching, query.data, query.dataUpdatedAt]);

  useEffect(() => {
    if (!transientNotice) return undefined;
    const timer = window.setTimeout(() => setTransientNotice(null), 3000);
    return () => window.clearTimeout(timer);
  }, [transientNotice]);

  const syncStatus = deriveConversationListSyncStatus({
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    hadCachedData,
    transientNotice,
  });

  return {
    ...query,
    hadCachedData,
    cachedAt: cacheEntry?.fetchedAt ?? null,
    syncStatus,
    refresh: query.refetch,
  };
}
